import classNames from 'classnames';
import Icon from 'components/shared/icon';
import { useGlobalModules } from 'contexts';
import { formatPriceByStoreLocale } from 'lib/shopify';
import useSubscriptionPrice from 'hooks/shop/useSubscriptionPrice';
import styles from './subscription.module.scss';

const parseFrequency = option => {
  const [, interval, unit] = option?.value?.match(/^(\d+)\s*(.*)$/) || [];
  return { interval: interval || option?.value, unit: interval ? unit : '' };
};

const Subscription = ({ shopifyData, sellingPlan, setSellingPlan, locale }) => {
  const sellingPlans = shopifyData?.sellingPlans || [];
  const globalModules = useGlobalModules();
  const getSubscriptionPrice = useSubscriptionPrice(locale, shopifyData);
  const { hasWebshop, hasSubscriptions } = globalModules.settings.locale;
  const { title, infoButtonText, oneTimePurchaseLabel, ongoingSubscriptionsLabel, usps } =
    globalModules.translations.subscription || {};
  const deliveryFrequencyLabel =
    globalModules.translations.accessibility?.deliveryFrequency || 'Delivery frequency';
  const originalPriceLabel =
    globalModules.translations.accessibility?.originalPrice || 'Original price';

  if (!hasWebshop || hasSubscriptions === false || !sellingPlans.length) return null;

  const price = parseFloat(shopifyData.productPrice);
  const activePlan = sellingPlan || sellingPlans[0];
  const planPrice = getSubscriptionPrice(activePlan);
  const isSubscription = Boolean(sellingPlan);
  const firstOption = activePlan.options?.[0];
  const { unit } = parseFrequency(firstOption);
  const uspList = [].concat(usps || []).filter(Boolean);

  return (
    <div className={styles.container}>
      <p className="sr-only" id="subscription-title">
        {title || infoButtonText}
      </p>
      <div role="radiogroup" aria-labelledby="subscription-title" className={styles.options}>
        <label
          htmlFor="purchase-one-time"
          className={classNames(styles.option, { [styles.active]: !isSubscription })}
        >
          <input
            id="purchase-one-time"
            className={styles.radio}
            type="radio"
            name="purchaseType"
            checked={!isSubscription}
            onChange={() => setSellingPlan(null)}
          />
          <span className={styles.label}>{oneTimePurchaseLabel}</span>
          <span className={styles.price}>{formatPriceByStoreLocale({ locale, price })}</span>
        </label>
        <div
          className={classNames(styles.option, styles.subscription, {
            [styles.active]: isSubscription,
          })}
        >
          <label htmlFor="purchase-subscription" className={styles.optionHeader}>
            <input
              id="purchase-subscription"
              className={styles.radio}
              type="radio"
              name="purchaseType"
              checked={isSubscription}
              onChange={() => setSellingPlan(activePlan)}
            />
            <span className={styles.label}>{ongoingSubscriptionsLabel}</span>
            <span className={styles.price}>
              <strong>{formatPriceByStoreLocale({ locale, price: planPrice })}</strong>
              {planPrice < price && (
                <s>
                  <span className="sr-only">{originalPriceLabel} </span>
                  {formatPriceByStoreLocale({ locale, price })}
                </s>
              )}
            </span>
          </label>
          <div className={styles.frequency}>
            {firstOption?.name && <span className={styles.frequencyName}>{firstOption.name}</span>}
            <div role="group" aria-label={deliveryFrequencyLabel} className={styles.pills}>
              {sellingPlans.map(plan => (
                <button
                  key={plan.id}
                  type="button"
                  className={classNames(styles.pill, {
                    [styles.pillActive]: isSubscription && plan.id === activePlan.id,
                  })}
                  aria-pressed={isSubscription && plan.id === activePlan.id}
                  aria-label={plan.name}
                  onClick={() => setSellingPlan(plan)}
                >
                  {parseFrequency(plan.options?.[0]).interval}
                </button>
              ))}
            </div>
            {unit && <span>{unit}</span>}
          </div>
          {uspList.length > 0 && (
            <ul className={styles.usps}>
              {uspList.map(usp => (
                <li key={usp}>
                  <Icon type="check" />
                  {usp}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default Subscription;
